/**
 * Start a review: add the documents (drop or pick files; from iManage when
 * it is connected), choose what they are checked against, name it, run.
 *
 * Each file is read in the browser as it is added (`extractDocumentText`),
 * so a scan without text or a damaged file is reported on its own row before
 * anything is sent. Running creates the review and starts its eve run in one
 * request, then opens the review page, where results arrive as they are
 * found.
 *
 * @module components/apps/contractIntelligence/CiNewReviewPage
 */
import { Link, useNavigate } from "@tanstack/react-router";
import { AlertCircleIcon, FileTextIcon, Loader2Icon, PlayIcon, UploadIcon, XIcon } from "lucide-react";
import { useCallback, useEffect, useLayoutEffect, useRef, useState, type DragEvent } from "react";

import { cn } from "../../../lib/utils";
import { createCiReview, useCiSettings, type CiBasis } from "../../../state/contractIntelligence";
import { useIManageStatus } from "../../../state/imanage";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { BasisPicker } from "./BasisPicker";
import { fileSizeLabel } from "./ciModel";
import { CiPage } from "./ciParts";
import { ACCEPTED_DOCUMENTS, extractDocumentText, type ExtractedDocument } from "./extractDocumentText";
import { IManagePickDialog } from "./IManagePickDialog";
import { useCiOrg } from "./useCiOrg";

const MAX_DOCUMENTS = 10;

interface Entry {
  readonly key: string;
  readonly name: string;
  readonly size: number;
  readonly source: "upload" | "imanage";
  readonly state: { readonly kind: "reading" } | { readonly kind: "ready"; readonly document: ExtractedDocument } | { readonly kind: "error"; readonly message: string };
  readonly counterparty: string;
}

let entrySeed = 0;

function Step({ number, title, detail, children }: { readonly number: number; readonly title: string; readonly detail?: string; readonly children: React.ReactNode }) {
  return (
    <section className="grid gap-3 sm:grid-cols-[2rem_1fr]" aria-labelledby={`ci-step-${number}`}>
      <span className="hidden size-6 items-center justify-center rounded-full border border-border/80 text-[11px] font-medium tabular-nums text-muted-foreground sm:flex">
        {number}
      </span>
      <div className="min-w-0 space-y-3">
        <div>
          <h2 id={`ci-step-${number}`} className="text-[13px] font-semibold">
            {title}
          </h2>
          {detail ? <p className="text-xs text-muted-foreground">{detail}</p> : null}
        </div>
        {children}
      </div>
    </section>
  );
}

export function CiNewReviewPage() {
  const { organizationId, orgSegment } = useCiOrg();
  const navigate = useNavigate();
  const settings = useCiSettings(organizationId);
  const imanage = useIManageStatus(organizationId);
  const [entries, setEntries] = useState<ReadonlyArray<Entry>>([]);
  const [basis, setBasis] = useState<CiBasis | null>(null);
  const [basisTouched, setBasisTouched] = useState(false);
  const [title, setTitle] = useState("");
  const [dragging, setDragging] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [picking, setPicking] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // The organization's default basis, once, unless the person already chose.
  const defaultBasis = settings.read?.kind === "ready" ? settings.read.value.defaultBasis : null;
  useEffect(() => {
    if (!basisTouched && defaultBasis && basis === null) setBasis(defaultBasis);
  }, [basis, basisTouched, defaultBasis]);

  // The committed count, kept in step after each commit (never from a render React may
  // discard); addFiles advances it at once, so two drops in one tick share the room.
  const countRef = useRef(0);
  useLayoutEffect(() => {
    countRef.current = entries.length;
  }, [entries.length]);
  const addFiles = useCallback((files: ReadonlyArray<File>, source: Entry["source"] = "upload") => {
    setError(null);
    const room = MAX_DOCUMENTS - countRef.current;
    if (room <= 0) {
      setError(`A review takes at most ${MAX_DOCUMENTS} documents.`);
      return;
    }
    if (files.length > room) {
      setError(`Only the first ${room} file${room === 1 ? " was" : "s were"} added: a review takes at most ${MAX_DOCUMENTS}.`);
    }
    const added = files.slice(0, room).map((file) => ({ key: `doc-${(entrySeed += 1)}`, file }));
    countRef.current += added.length;
    setEntries((current) => [
      ...current,
      ...added.map(({ key, file }): Entry => ({ key, name: file.name, size: file.size, source, state: { kind: "reading" }, counterparty: "" })),
    ]);
    for (const { key, file } of added) {
      void extractDocumentText(file).then(
        (document) => setEntries((list) => list.map((item) => (item.key === key ? { ...item, state: { kind: "ready", document } } : item))),
        (cause: unknown) =>
          setEntries((list) =>
            list.map((item) =>
              item.key === key
                ? { ...item, state: { kind: "error", message: cause instanceof Error ? cause.message : "This file could not be read." } }
                : item,
            ),
          ),
      );
    }
  }, []);

  const onDrop = (event: DragEvent<HTMLElement>) => {
    event.preventDefault();
    setDragging(false);
    addFiles([...event.dataTransfer.files]);
  };

  const ready = entries.filter((entry): entry is Entry & { state: { kind: "ready"; document: ExtractedDocument } } => entry.state.kind === "ready");
  const reading = entries.some((entry) => entry.state.kind === "reading");
  const blocker =
    entries.length === 0
      ? "Add at least one document."
      : reading
        ? "Wait for the documents to be read."
        : ready.length === 0
          ? "None of the documents could be read."
          : basis === null
            ? "Choose what to check against."
            : null;

  const run = async () => {
    if (!organizationId || !orgSegment || blocker || !basis) return;
    setBusy(true);
    setError(null);
    const outcome = await createCiReview(organizationId, {
      title: title.trim() || null,
      basis,
      run: true,
      documents: ready.map((entry) => ({ ...entry.state.document, counterparty: entry.counterparty.trim() || null })),
    });
    if (!outcome.ok) {
      setBusy(false);
      setError(outcome.message);
      return;
    }
    void navigate({ to: "/$org/apps/contract-intelligence/reviews/$reviewId", params: { org: orgSegment, reviewId: outcome.value.id } });
  };

  return (
    <CiPage title="New review" data-ci-page="new">
      <div className="mx-auto w-full max-w-3xl space-y-8 px-4 py-6 sm:px-6 sm:py-8">
        <Step number={1} title="Documents" detail="PDF, Word (.docx), text or Markdown. Up to 10 per review, 25 MB each.">
          <div
            onDragOver={(event) => {
              event.preventDefault();
              setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={onDrop}
            className={cn(
              "flex flex-col items-center gap-2 rounded-xl border border-dashed px-4 py-7 text-center transition-colors",
              dragging ? "border-primary bg-primary/5" : "border-border bg-muted/20",
            )}
            data-ci-dropzone=""
          >
            <UploadIcon aria-hidden className="size-5 text-muted-foreground" />
            <p className="text-[13px]">
              Drop documents here, or{" "}
              <button type="button" className="font-medium underline underline-offset-2 outline-none hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring rounded" onClick={() => inputRef.current?.click()}>
                choose files
              </button>
            </p>
            {imanage.status?.connected ? (
              <Button size="xs" variant="outline" onClick={() => setPicking(true)}>
                Add from iManage
              </Button>
            ) : null}
            <input
              ref={inputRef}
              type="file"
              multiple
              accept={ACCEPTED_DOCUMENTS}
              className="sr-only"
              tabIndex={-1}
              aria-hidden
              onChange={(event) => {
                addFiles([...(event.currentTarget.files ?? [])]);
                event.currentTarget.value = "";
              }}
            />
          </div>
          {entries.length > 0 ? (
            <ul className="divide-y divide-border/50 overflow-hidden rounded-xl border border-border/70 bg-card" aria-label="Documents to review">
              {entries.map((entry) => (
                <li key={entry.key} className="flex flex-col gap-2 px-3 py-2.5 sm:flex-row sm:items-center sm:gap-3" data-ci-entry={entry.state.kind}>
                  <span className="flex min-w-0 flex-1 items-center gap-2.5">
                    {entry.state.kind === "reading" ? (
                      <Loader2Icon aria-hidden className="size-4 shrink-0 animate-spin text-muted-foreground" />
                    ) : entry.state.kind === "error" ? (
                      <AlertCircleIcon aria-hidden className="size-4 shrink-0 text-destructive" />
                    ) : (
                      <FileTextIcon aria-hidden className="size-4 shrink-0 text-muted-foreground" />
                    )}
                    <span className="min-w-0">
                      <span className="block truncate text-[13px] font-medium">{entry.name}</span>
                      <span className={cn("block truncate text-xs", entry.state.kind === "error" ? "text-destructive" : "text-muted-foreground")}>
                        {entry.state.kind === "reading"
                          ? "Reading…"
                          : entry.state.kind === "error"
                            ? entry.state.message
                            : `${fileSizeLabel(entry.size)} · ${entry.state.document.text.length.toLocaleString("en-GB")} characters${entry.source === "imanage" ? " · iManage" : ""}`}
                      </span>
                    </span>
                  </span>
                  {entry.state.kind !== "error" ? (
                    <Input
                      aria-label={`Counterparty for ${entry.name}`}
                      value={entry.counterparty}
                      onChange={(event) => {
                        const counterparty = event.currentTarget.value;
                        setEntries((list) => list.map((item) => (item.key === entry.key ? { ...item, counterparty } : item)));
                      }}
                      placeholder="Counterparty (optional)"
                      size="sm"
                      className="sm:w-56"
                    />
                  ) : null}
                  <Button
                    size="icon-sm"
                    variant="ghost"
                    aria-label={`Remove ${entry.name}`}
                    className="self-end sm:self-auto"
                    onClick={() => setEntries((list) => list.filter((item) => item.key !== entry.key))}
                  >
                    <XIcon aria-hidden />
                  </Button>
                </li>
              ))}
            </ul>
          ) : null}
        </Step>

        <Step number={2} title="Check against" detail="Your policies, a regime you follow, or one of your templates.">
          {organizationId ? (
            <BasisPicker
              organizationId={organizationId}
              value={basis}
              onChange={(next) => {
                setBasisTouched(true);
                setBasis(next);
              }}
            />
          ) : null}
        </Step>

        <Step number={3} title="Name" detail="Optional. Defaults to the first document's name.">
          <Input
            aria-label="Review name"
            value={title}
            onChange={(event) => setTitle(event.currentTarget.value)}
            placeholder={ready[0]?.name.replace(/\.[a-z0-9]{1,5}$/i, "") ?? "Orion Credit Fund side letter"}
          />
        </Step>

        <div className="flex flex-col-reverse gap-3 border-t border-border/60 pt-5 sm:flex-row sm:items-center">
          <p className={cn("flex-1 text-xs", error ? "text-destructive" : "text-muted-foreground")} role={error ? "alert" : undefined} aria-live="polite">
            {error ?? blocker ?? "Amoreg reads each clause against the basis. Results appear on the review page as they are found."}
          </p>
          <div className="flex shrink-0 gap-2">
            {orgSegment ? (
              <Button variant="ghost" size="sm" render={<Link to="/$org/apps/contract-intelligence" params={{ org: orgSegment }} />}>
                Cancel
              </Button>
            ) : null}
            <Button size="sm" className="gap-1.5" disabled={blocker !== null || busy} onClick={() => void run()} data-ci-run="">
              {busy ? <Loader2Icon aria-hidden className="size-3.5 animate-spin" /> : <PlayIcon aria-hidden className="size-3.5" />}
              {busy ? "Starting…" : "Run review"}
            </Button>
          </div>
        </div>
      </div>
      {picking && organizationId ? (
        <IManagePickDialog
          organizationId={organizationId}
          onClose={() => setPicking(false)}
          onPick={(files) => {
            setPicking(false);
            addFiles(files, "imanage");
          }}
        />
      ) : null}
    </CiPage>
  );
}
